import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { TextTabs } from 'components/tabs/TextTabs';
import { useMediaQuery } from 'hooks';

import { Accordion } from '../accordion/Accordion';
import styles from './Showcase.module.css';

type Props = PropsWithChildren<{
  title: string | JSX.Element;
  size: 'small' | 'large';
  /** Помогает определить цветовую схему для аккардиона */
  colorSheme?: string;
  tabs: Array<{
    title: string;
    text: string;
    imageUrl?: string;
  }>;
}>;

export const Showcase: FC<Props> = ({
  title,
  size,
  tabs,
  children,
  colorSheme = 'black',
}) => {
  const timerScroll = useRef<NodeJS.Timeout | null>(null);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const isLaptop = useMediaQuery('(max-width: 904px)');

  const onToggle = (idx: number) => {
    setActiveTabIndex(idx);
    if (timerScroll.current) {
      clearInterval(timerScroll.current);
    }
  };

  useEffect(() => {
    timerScroll.current = setInterval(() => {
      setActiveTabIndex(prev => (prev + 1 === tabs.length ? 0 : prev + 1));
    }, 5000);
    return () => {
      if (timerScroll.current) {
        clearInterval(timerScroll.current);
      }
    };
  }, [tabs]);

  const contentAccordion = useMemo(() => {
    if (size !== 'small') {
      return tabs.map(i => ({ ...i, imageUrl: '' }));
    }
    return tabs;
  }, [tabs, size]);

  const renderLarge = () => {
    return (
      <>
        <div className={styles.content_large}>
          <h2 className={styles.tile_showcase}>{title}</h2>
          <div className={styles.content_accordion}>
            <div className={styles.content_accordion_body}>
              <Accordion
                play
                items={contentAccordion}
                size="small"
                defaultIndex={activeTabIndex}
                colorSheme={colorSheme}
                onChange={onToggle}
              />
              {children}
            </div>
          </div>
        </div>
        <div className={styles.image_wrapper}>
          {tabs.map((i, key) => {
            return (
              <img
                key={i.imageUrl}
                className={classNames(styles.image_large, {
                  [styles.image_small_active]: activeTabIndex === key,
                })}
                loading="lazy"
                src={i.imageUrl}
                alt="tab"
              />
            );
          })}
        </div>
      </>
    );
  };

  const renderSmall = () => {
    return (
      <div className={styles.content_small}>
        <h2>{title}</h2>
        <Accordion
          play
          items={contentAccordion}
          size="small"
          defaultIndex={activeTabIndex}
          colorSheme={colorSheme}
          onChange={onToggle}
        />
      </div>
    );
  };

  const renderTabs = () => {
    return (
      <div className={styles.content_small}>
        <h2>{title}</h2>
        <TextTabs
          items={contentAccordion}
          defaultIndex={activeTabIndex}
          onChange={onToggle}
        >
          {children}
        </TextTabs>
      </div>
    );
  };

  const content = () => {
    if (size === 'small') {
      return !isLaptop ? renderTabs() : renderSmall();
    }
    return renderLarge();
  };

  return <div className={styles.container}>{content()}</div>;
};
